/*
 * Authors: mariushirjanu
 * Created: 02/09/2021
 */

import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { FormattingService } from '../../shared/services/formatting.service';
import { PaymentFormattingService } from './payment-formatting.service';
import { PAYMENT_STATUS_OPEN, PAYMENT_STATUS_REFUSED, PaymentDataService } from './payment-data.service';
import { IInvoiceHeader } from '../../../type_definitions/datatypes';
import { UntypedFormGroup } from '@angular/forms';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../../shared/formly/ga-formly.service';
import { InvoiceDataService } from '../../shared/invoice-info/invoice-data.service';

interface IInvoicePaymentModel {
  paymentTotal?:string;
  paymentStatus?:string;
}

@Component({
  selector: 'invoice-payment',
  template: `
    <h2 *ngIf="loading">
      <activity-indicator toLoad="ORDERENTRY.PAYMENT.TITLE"></activity-indicator>
    </h2>
    <div *ngIf="!loading">
      <h3>Payment amount: {{model.paymentTotal}}</h3>
      <div *ngIf="ccButtonVisible || ppButtonVisible" [innerHTML]="'ORDERENTRY.PAYMENT.CREDIT_CARD_CAVEAT' | translate"></div>
      <formly [form]="form" [fields]="fields" [model]="model" [noValidate]="true">
        <button class="btn btn-success" (click)="payWithCreditCard()" [busyButton]="buttonBusy" *ngIf="ccButtonVisible" [bbReplaceIcon]="true"><i class="fa fa-credit-card margin-right-xs"></i> <span translate>ORDERENTRY.PAYMENT.PAY_CC</span></button>
        <button class="btn btn-primary" (click)="payWithPayPal()" [busyButton]="buttonBusy" *ngIf="ppButtonVisible" [bbReplaceIcon]="true"><i class="fa fa-paypal margin-right-xs"></i> <span translate>ORDERENTRY.PAYMENT.PAY_PP</span></button>
      </formly>
    </div>
  `
})
export class InvoicePaymentComponent implements OnInit, OnChanges {
  @Input() public invoice:number;

  public form:UntypedFormGroup = new UntypedFormGroup({});
  public fields:IExtraFieldConfigurationObject[];
  public loading:boolean;
  public ccButtonVisible:boolean = true;
  public ppButtonVisible:boolean = true;
  public buttonBusy:boolean = false;
  public model:IInvoicePaymentModel;

  private data:IInvoiceHeader;

  constructor(
    private formlyService:GaFormlyService,
    private formattingService:FormattingService,
    private paymentDataService:PaymentDataService,
    private invoiceDataService:InvoiceDataService,
    private paymentFormattingService:PaymentFormattingService
  ) {
    /***/
  }

  public ngOnInit():void {
    this.loadInvoice();
    this.fields = this.createFields();
  }

  private createFields():IExtraFieldConfigurationObject[] {
    const fields:IExtraFieldConfigurationObject[] = [];
    fields.push(
      this.formlyService.aValue('paymentStatus', 'ORDERENTRY.PAYMENT.STATUS')
        .withFormat(paymentStatus => this.paymentFormattingService.formatPaymentStatus(paymentStatus, undefined, this.data.paymentTotal, this.data.paymentCurrency, this.data.paymentDate))
        .build());
    return fields;
  }

  private updateFormState = ():void => {
    this.ccButtonVisible = false;
    this.ppButtonVisible = false;
    // this means the credit card is not cleared or clearing yet
    if (this.model && (this.model.paymentStatus === PAYMENT_STATUS_OPEN || this.model.paymentStatus === PAYMENT_STATUS_REFUSED)) {
      const paymentType:string = this.data && this.data.paymentType;
      if (paymentType === 'CC') {
        this.ccButtonVisible = true;
      } else if (paymentType === 'PP') {
        this.ppButtonVisible = true;
      }
    }
  };

  private loadInvoice():void {
    this.loading = true;

    this.invoiceDataService.loadInvoiceHeader(this.invoice)
      .then((invoiceHeader:IInvoiceHeader) => {
        this.data = invoiceHeader;
        this.model = {
          paymentStatus: invoiceHeader.paymentStatus,
          paymentTotal: this.formattingService.formatAmount(invoiceHeader.paymentTotal, invoiceHeader.paymentCurrency)
        };
      })
      .then(this.updateFormState)
      .finally(() => {
        this.loading = false;
      });
  }

  public payWithCreditCard():void {
    this.buttonBusy = true;
    this.paymentDataService.payWithStripe(undefined, this.invoice, window.top.location.href)
      .finally(() => {
        this.buttonBusy = false;
      });
  }

  public payWithPayPal():void {
    this.buttonBusy = true;
    this.paymentDataService.payWithPayPal(undefined, this.invoice, window.top.location.href)
      .finally(() => {
        this.buttonBusy = false;
      });
  }

  public ngOnChanges(changesObj:SimpleChanges):void {
    if (changesObj.invoice) {
      this.invoice = changesObj.invoice.currentValue;
      this.loadInvoice();
    }
  }
}